import { Modal, Table } from 'antd';
import dayjs from 'dayjs';
import React from 'react';
import {
  PostMemberEngagementMetrics,
  NotEngagedMemberMetrics,
} from 'src/api/engagements';

interface MemberEngagementModalProps {
  isVisible: boolean;
  postTitle: string;
  modalType: 'completed' | 'comments' | 'not-engaged';
  postViewers: PostMemberEngagementMetrics[];
  postNotEngagedMembers: NotEngagedMemberMetrics[];
  loading: boolean;
  onClose: () => void;
}

export const MemberEngagementModal: React.FC<MemberEngagementModalProps> = ({
  isVisible,
  postTitle,
  modalType,
  postViewers,
  postNotEngagedMembers,
  loading,
  onClose,
}) => {
  const modalColumns = [
    {
      title: 'Member',
      dataIndex: ['member', 'user', 'full_name'],
      key: 'member_name',
    },
    {
      title: 'Last Viewed',
      dataIndex: ['engagement', 'lastViewedAt'],
      key: 'lastViewedAt',
      render: (date: string) => (date ? dayjs(date).format('MM/DD/YYYY') : '-'),
    },
    {
      title: 'Duration',
      dataIndex: ['engagement'],
      key: 'duration',
      render: (engagement: any) => {
        const { duration, videoLength } = engagement;
        if (!duration) {
          return '-';
        }

        if (videoLength && videoLength > 0) {
          const percentage = Math.round((duration / videoLength) * 100);
          return `${percentage}%`;
        }

        return `${Math.round(duration / 1000)}s`;
      },
    },
    {
      title: 'Comments',
      dataIndex: ['engagement', 'comments'],
      key: 'comments',
    },
    {
      title: 'Member Since',
      dataIndex: ['member', 'createdAt'],
      key: 'member-since',
      render: (date: string) => dayjs(date).format('MM/DD/YYYY'),
    },
  ];

  const notEngagedColumns = [
    {
      title: 'Member',
      dataIndex: ['member', 'user', 'full_name'],
      key: 'member_name',
      render: (name: string, record: NotEngagedMemberMetrics) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <img
            src={record.member.user.avatar_url}
            alt={name}
            style={{
              width: '24px',
              height: '24px',
              borderRadius: '50%',
              objectFit: 'cover',
            }}
          />
          {name}
        </div>
      ),
    },
    {
      title: 'Last Activity',
      dataIndex: 'lastActivity',
      key: 'lastActivity',
      render: (date: string) =>
        date ? (
          dayjs(date).format('MM/DD/YYYY')
        ) : (
          <span style={{ color: '#ff4d4f' }}>Never</span>
        ),
    },
    {
      title: 'Days Inactive',
      dataIndex: 'daysSinceLastActivity',
      key: 'daysSinceLastActivity',
      render: (days: number | undefined) =>
        days !== undefined ? (
          `${days} days`
        ) : (
          <span style={{ color: '#ff4d4f' }}>N/A</span>
        ),
    },
    {
      title: 'Total Engagements',
      dataIndex: 'totalEngagements',
      key: 'totalEngagements',
    },
    {
      title: 'Member Since',
      dataIndex: ['member', 'createdAt'],
      key: 'member-since',
      render: (date: string) => dayjs(date).format('MM/DD/YYYY'),
    },
  ];

  const getModalTitle = () => {
    switch (modalType) {
      case 'completed':
        return `Member Engagement - ${postTitle} (Views)`;
      case 'comments':
        return `Member Engagement - ${postTitle} (Comments)`;
      case 'not-engaged':
        return `Not Engaged Members - ${postTitle}`;
      default:
        return `Member Engagement - ${postTitle}`;
    }
  };

  return (
    <Modal
      title={getModalTitle()}
      open={isVisible}
      onCancel={onClose}
      footer={null}
      width={800}
    >
      {modalType === 'not-engaged' ? (
        <Table
          dataSource={postNotEngagedMembers}
          columns={notEngagedColumns}
          loading={loading}
          pagination={false}
          rowKey={(record) => record.member._id}
          size="small"
        />
      ) : (
        <Table
          dataSource={postViewers}
          columns={modalColumns}
          loading={loading}
          pagination={false}
          rowKey={(record) => record.member._id}
          size="small"
        />
      )}
    </Modal>
  );
};
